import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Toggle from '@mulesoft/anypoint-components/lib/Toggle';
import Pill from '@mulesoft/anypoint-components/lib/Pill';
import { validateEmail } from '../utils';
import styles from '../ScannerConfigurationForm.css';

/**
 * AdvancedSettingsSection Component
 *
 * Renders the advanced settings accordion with:
 * - Email notifications toggle
 * - Recipient emails input with chip-style display
 */
const AdvancedSettingsSection = ({
  showAdvancedSettings,
  onToggleAdvancedSettings,
  emailNotificationsEnabled,
  onToggleEmailNotifications,
  recipientEmails,
  onAddEmail,
  onRemoveEmail
}) => {
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');

  // Helper function to focus the email input field
  const focusEmailInput = useCallback(() => {
    const input = document.getElementById('recipient-emails');

    if (input) {
      input.focus();
    }
  }, []);

  // Add single email to recipient list
  const addSingleEmail = useCallback(
    (email) => {
      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        return false;
      }

      if (
        !validateEmail ||
        typeof validateEmail !== 'function' ||
        !validateEmail(trimmedEmail)
      ) {
        setEmailInputError('Invalid email format');

        return false;
      }

      if (recipientEmails.includes(trimmedEmail)) {
        setEmailInputError('Email already added');

        return false;
      }

      setEmailInputError('');
      onAddEmail(trimmedEmail);

      return true;
    },
    [recipientEmails, onAddEmail]
  );

  // Handle new email input change
  // Mulesoft TextField onChange provides { value } directly
  const handleNewEmailChange = useCallback(
    ({ value }) => {
      if (emailInputError) {
        setEmailInputError('');
      }

      setNewEmailInput(value || '');
    },
    [emailInputError]
  );

  // Handle paste event - parse comma-separated emails
  const handleEmailPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const emails = pastedText
        .split(/[,;\s]+/)
        .map((email) => email.trim())
        .filter((email) => email);

      emails.forEach((email) => {
        addSingleEmail(email);
      });

      setNewEmailInput('');
    },
    [addSingleEmail]
  );

  // Handle key down events (Enter, Tab, Space, Comma)
  const handleEmailKeyDown = useCallback(
    (e) => {
      if (
        e.key === 'Enter' ||
        e.key === 'Tab' ||
        e.key === ' ' ||
        e.key === ','
      ) {
        e.preventDefault();

        if (newEmailInput.trim()) {
          if (addSingleEmail(newEmailInput)) {
            setNewEmailInput('');
          }
        }
      }

      if (e.key === 'Backspace' && !newEmailInput) {
        if (recipientEmails.length > 0) {
          const lastEmail = recipientEmails[recipientEmails.length - 1];

          onRemoveEmail(lastEmail);
        }
      }
    },
    [newEmailInput, addSingleEmail, recipientEmails, onRemoveEmail]
  );

  // Handle blur event - create email pill when user changes focus
  const handleEmailBlur = useCallback(() => {
    if (newEmailInput.trim()) {
      if (addSingleEmail(newEmailInput)) {
        setNewEmailInput('');
      }
    }
  }, [newEmailInput, addSingleEmail]);

  return (
    <React.Fragment>
      {/* Advanced Settings Toggle */}
      <div className={styles.field}>
        <span
          className={styles['accordion-toggle']}
          onClick={onToggleAdvancedSettings}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleAdvancedSettings();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={showAdvancedSettings}
          aria-controls="advanced-settings-content"
          data-test-id="advanced-settings-toggle"
        >
          <span className={styles['accordion-icon']}>
            {showAdvancedSettings ? '▼' : '▶'}
          </span>{' '}
          Advanced Settings
        </span>
        <span className={styles['settings-description']}>
          Configure email notifications
        </span>
      </div>

      {showAdvancedSettings && (
        <div
          id="advanced-settings-content"
          className={styles['accordion-content']}
        >
          {/* Email Notifications Toggle */}
          <div className={styles.field}>
            <label
              htmlFor="email-notifications-toggle"
              className={styles.label}
            >
              Enable email notifications
            </label>
            <Toggle
              id="email-notifications-toggle"
              testId="email-notifications-toggle"
              checked={emailNotificationsEnabled}
              onChange={onToggleEmailNotifications}
            />
          </div>

          {/* Recipient Emails - Only show when notifications are enabled */}
          {emailNotificationsEnabled && (
            <div className={styles.field}>
              <label htmlFor="recipient-emails" className={styles.label}>
                Send email to
              </label>
              <div className={styles['field-content']}>
                {/* Gmail-style chip input container */}
                <div
                  className={`${styles['email-chip-container']} ${
                    emailInputError ? styles.error : ''
                  }`}
                  onClick={focusEmailInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      focusEmailInput();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {/* Email chips inside the container */}
                  {recipientEmails.map((email) => (
                    <Pill
                      key={email}
                      testId={`email-chip-${email}`}
                      className={styles['email-pill']}
                      onRemove={() => onRemoveEmail(email)}
                    >
                      {email}
                    </Pill>
                  ))}

                  {/* Transparent input field */}
                  <TextField
                    id="recipient-emails"
                    testId="recipient-emails-input"
                    type="text"
                    className={styles['email-chip-input']}
                    value={newEmailInput}
                    onChange={handleNewEmailChange}
                    onKeyDown={handleEmailKeyDown}
                    onBlur={handleEmailBlur}
                    onPaste={handleEmailPaste}
                    placeholder={
                      recipientEmails.length === 0
                        ? 'Enter email addresses...'
                        : ''
                    }
                  />
                </div>

                {/* Error message */}
                {emailInputError && (
                  <div className={styles['email-error-message']} role="alert">
                    {emailInputError}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

AdvancedSettingsSection.propTypes = {
  /** Whether advanced settings section is expanded */
  showAdvancedSettings: PropTypes.bool.isRequired,
  /** Handler to toggle advanced settings visibility */
  onToggleAdvancedSettings: PropTypes.func.isRequired,
  /** Whether email notifications are enabled */
  emailNotificationsEnabled: PropTypes.bool.isRequired,
  /** Handler to toggle email notifications */
  onToggleEmailNotifications: PropTypes.func.isRequired,
  /** Array of recipient email addresses */
  recipientEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Handler to add a new email */
  onAddEmail: PropTypes.func.isRequired,
  /** Handler to remove an email */
  onRemoveEmail: PropTypes.func.isRequired
};

export default AdvancedSettingsSection;
